// ── Scheduled Posts ──────────────────────────────────────────────
//
// Browser-based post scheduler. Stores drafts in localStorage and
// publishes them when the scheduled time arrives.

const STORAGE_KEY = 'bchnostr:scheduled-posts';

export interface ScheduledPost {
  /** Unique ID */
  id: string;
  /** Post content */
  content: string;
  /** Nostr event kind (1 = note, 1068 = poll) */
  kind: number;
  /** Event tags */
  tags: string[][];
  /** Unix timestamp (seconds) when the post should be published */
  scheduledAt: number;
  /** Unix timestamp (seconds) when the draft was created */
  createdAt: number;
  /** Status */
  status: 'pending' | 'published' | 'failed';
  /** Error message if failed */
  error?: string;
  /** Attachments for display in the queue */
  attachments?: { url: string; type: string }[];
}

// ── Storage ─────────────────────────────────────────────────────

function loadAll(): ScheduledPost[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function saveAll(posts: ScheduledPost[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(posts));
  } catch {}
}

// ── Public API ──────────────────────────────────────────────────

/** Get all scheduled posts (pending ones first, sorted by scheduled time) */
export function getScheduledPosts(): ScheduledPost[] {
  const all = loadAll();
  // Prune posts older than 7 days that are published/failed
  const cutoff = Date.now() / 1000 - 7 * 86400;
  const fresh = all.filter(p =>
    p.status === 'pending' || p.createdAt > cutoff
  );
  if (fresh.length !== all.length) saveAll(fresh);
  return fresh.sort((a, b) => {
    // Pending first
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (b.status === 'pending' && a.status !== 'pending') return 1;
    return a.scheduledAt - b.scheduledAt;
  });
}

/** Get only pending scheduled posts */
export function getPendingPosts(): ScheduledPost[] {
  return loadAll().filter(p => p.status === 'pending');
}

/** Schedule a new post */
export function schedulePost(post: Omit<ScheduledPost, 'id' | 'createdAt' | 'status'>): ScheduledPost {
  const newPost: ScheduledPost = {
    ...post,
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    createdAt: Math.floor(Date.now() / 1000),
    status: 'pending',
  };
  const all = loadAll();
  all.push(newPost);
  saveAll(all);
  notifyListeners();
  return newPost;
}

/** Cancel a scheduled post */
export function cancelScheduledPost(id: string): boolean {
  const all = loadAll();
  const idx = all.findIndex(p => p.id === id);
  if (idx === -1) return false;
  all.splice(idx, 1);
  saveAll(all);
  notifyListeners();
  return true;
}

/** Reschedule a pending post to a new time */
export function reschedulePost(id: string, newScheduledAt: number): boolean {
  const all = loadAll();
  const post = all.find(p => p.id === id);
  if (!post || post.status !== 'pending') return false;
  post.scheduledAt = newScheduledAt;
  saveAll(all);
  notifyListeners();
  return true;
}

/** Retry a failed post — resets it to pending with a new scheduled time (now + 10s) */
export function retryPost(id: string): boolean {
  const all = loadAll();
  const post = all.find(p => p.id === id);
  if (!post || post.status !== 'failed') return false;
  post.status = 'pending';
  post.error = undefined;
  post.scheduledAt = Math.floor(Date.now() / 1000) + 10; // retry in 10 seconds
  saveAll(all);
  notifyListeners();
  return true;
}

/** Edit the content of a pending post */
export function editScheduledPost(id: string, updates: { content?: string; scheduledAt?: number }): boolean {
  const all = loadAll();
  const post = all.find(p => p.id === id);
  if (!post || post.status !== 'pending') return false;
  if (updates.content !== undefined) post.content = updates.content;
  if (updates.scheduledAt !== undefined) post.scheduledAt = updates.scheduledAt;
  saveAll(all);
  notifyListeners();
  return true;
}

/** Get a single scheduled post by ID */
export function getScheduledPost(id: string): ScheduledPost | undefined {
  return loadAll().find(p => p.id === id);
}

/** Mark a post as published */
export function markPublished(id: string) {
  const all = loadAll();
  const post = all.find(p => p.id === id);
  if (post) {
    post.status = 'published';
    saveAll(all);
    notifyListeners();
    notifyPublished(post);
  }
}

/** Mark a post as failed */
export function markFailed(id: string, error: string) {
  const all = loadAll();
  const post = all.find(p => p.id === id);
  if (post) {
    post.status = 'failed';
    post.error = error;
    saveAll(all);
    notifyListeners();
  }
}

// ── Change listeners (for React re-renders) ─────────────────────

type Listener = () => void;
const listeners = new Set<Listener>();

export function onScheduleChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notifyListeners() {
  listeners.forEach(fn => fn());
}

// ── Publish notification listeners ──────────────────────────────

type PublishListener = (post: ScheduledPost) => void;
const publishListeners = new Set<PublishListener>();

/** Register a listener for when a scheduled post gets published */
export function onPostPublished(fn: PublishListener): () => void {
  publishListeners.add(fn);
  return () => publishListeners.delete(fn);
}

function notifyPublished(post: ScheduledPost) {
  publishListeners.forEach(fn => fn(post));
}

// ── Auto-publisher (runs in the background) ─────────────────────

let publisherRunning = false;
let publisherInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Start the background scheduler that checks for due posts every 15 seconds.
 * Requires a publish function (from useNostrPublish) to actually send events.
 */
export function startScheduler(
  publishFn: (event: { kind: number; content: string; tags: string[][]; created_at: number }) => Promise<any>,
) {
  if (publisherRunning) return;
  publisherRunning = true;

  const tick = async () => {
    const now = Math.floor(Date.now() / 1000);
    const pending = getPendingPosts();

    for (const post of pending) {
      if (post.scheduledAt <= now) {
        try {
          await publishFn({
            kind: post.kind,
            content: post.content,
            tags: post.tags,
            created_at: now, // Use current time, not the scheduled time
          });
          markPublished(post.id);
        } catch (err) {
          markFailed(post.id, err instanceof Error ? err.message : 'Publish failed');
        }
      }
    }
  };

  // Run immediately, then every 15 seconds
  tick();
  publisherInterval = setInterval(tick, 15_000);
}

/** Stop the background scheduler */
export function stopScheduler() {
  if (publisherInterval) {
    clearInterval(publisherInterval);
    publisherInterval = null;
  }
  publisherRunning = false;
}

// ── Helpers ─────────────────────────────────────────────────────

/** Format a scheduled time for display */
export function formatScheduledTime(unixSeconds: number): string {
  const date = new Date(unixSeconds * 1000);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (isToday) return `Today at ${time}`;
  if (isTomorrow) return `Tomorrow at ${time}`;
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${time}`;
}

/** Get time remaining as a human-readable string */
export function timeUntil(unixSeconds: number): string {
  const diff = unixSeconds - Math.floor(Date.now() / 1000);
  if (diff <= 0) return 'now';
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(diff / 86400);
  return `${d}d`;
}

/** Get quick schedule preset options */
export function getSchedulePresets(): { label: string; getTimestamp: () => number }[] {
  return [
    {
      label: 'In 30 min',
      getTimestamp: () => Math.floor(Date.now() / 1000) + 30 * 60,
    },
    {
      label: 'In 1 hour',
      getTimestamp: () => Math.floor(Date.now() / 1000) + 3600,
    },
    {
      label: 'In 3 hours',
      getTimestamp: () => Math.floor(Date.now() / 1000) + 3 * 3600,
    },
    {
      label: 'Tomorrow 9 AM',
      getTimestamp: () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
        return Math.floor(d.getTime() / 1000);
      },
    },
    {
      label: 'Tomorrow 12 PM',
      getTimestamp: () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(12, 0, 0, 0);
        return Math.floor(d.getTime() / 1000);
      },
    },
    {
      label: 'Tomorrow 6 PM',
      getTimestamp: () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(18, 0, 0, 0);
        return Math.floor(d.getTime() / 1000);
      },
    },
  ];
}

